import React from "react";
import { ArrowRight, Play } from "lucide-react";
import { Button } from "./ui/button";

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[#F8FAFC]">
        <div className="absolute top-0 right-0 w-1/2 h-full">
          <div className="absolute inset-0 bg-gradient-to-l from-[#0A192F]/5 to-transparent" />
        </div>
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(#0A192F 1px, transparent 1px), linear-gradient(90deg, #0A192F 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Overline */}
            <div className="animate-fade-in-up opacity-0 stagger-1">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/70">
                <span className="w-8 h-px bg-[#D4AF37]" />
                Empowerment Through Purpose
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="animate-fade-in-up opacity-0 stagger-2 text-4xl sm:text-5xl lg:text-6xl font-light text-[#0A192F] tracking-tight font-['Outfit']">
              One Prestigious Mission
              <span className="block mt-2 font-medium">
                Many Paths to{" "}
                <span className="relative inline-block">
                  Empowerment
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-[#D4AF37]" />
                </span>
              </span>
            </h1>

            {/* Subtitle */}
            <p className="animate-fade-in-up opacity-0 stagger-3 text-lg text-[#475569] max-w-xl leading-relaxed">
              A family of brands united by purpose — driving impact across nonprofit outreach, 
              travel experiences, marketing innovation, technology solutions, and financial empowerment.
            </p>

            {/* CTA Buttons */}
            <div className="animate-fade-in-up opacity-0 stagger-4 flex flex-wrap gap-4 pt-4">
              <Button
                onClick={() => scrollToSection("#divisions")}
                className="btn-primary group"
                data-testid="hero-explore-btn"
              >
                Explore Our Companies
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                onClick={() => scrollToSection("#contact")}
                className="btn-secondary"
                data-testid="hero-contact-btn"
              >
                Get in Touch
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="animate-fade-in-up opacity-0 stagger-5 pt-8 flex items-center gap-8">
              <div className="flex flex-col">
                <span className="text-3xl font-light text-[#0A192F] font-['Outfit']">9</span>
                <span className="text-sm text-[#475569]">Brand Divisions</span>
              </div>
              <div className="w-px h-12 bg-slate-200" />
              <div className="flex flex-col">
                <span className="text-3xl font-light text-[#0A192F] font-['Outfit']">10K+</span>
                <span className="text-sm text-[#475569]">Lives Impacted</span>
              </div>
              <div className="w-px h-12 bg-slate-200" />
              <div className="flex flex-col">
                <span className="text-3xl font-light text-[#0A192F] font-['Outfit']">4</span>
                <span className="text-sm text-[#475569]">Charity Partners</span>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="animate-fade-in-up opacity-0 stagger-3 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.pexels.com/photos/36255884/pexels-photo-36255884.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Modern enterprise building representing Prestigious Companies"
                className="w-full h-[500px] lg:h-[600px] object-cover"
                data-testid="hero-image"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/30 to-transparent" />
              
              {/* Floating card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#0A192F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A192F] font-['Outfit']">Power + Purpose</p>
                    <p className="text-sm text-[#475569]">Building a better tomorrow, together</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-[#D4AF37]/30 rounded-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#0A192F]/5 rounded-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
